/**
 * Payroll vs Register analysis engine.
 * Spending entity = Employee Register department (not a separate payroll column).
 */

export function normId(v) {
  if (v == null || v === "") return "";
  return String(v).trim().replace(/\s+/g, "").toUpperCase();
}

export function normKeyHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function pickColumn(headers, patterns) {
  const list = (headers || []).map((h) => ({ raw: h, key: normKeyHeader(h) }));
  for (const p of patterns) {
    const pl = p.toLowerCase();
    const exact = list.find((x) => x.key === pl);
    if (exact) return exact.raw;
  }
  for (const p of patterns) {
    const pl = p.toLowerCase();
    const sub = list.find((x) => x.key.includes(pl));
    if (sub) return sub.raw;
  }
  return null;
}

export function parseMoney(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const s = String(v).replace(/,/g, "").replace(/[^\d.-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Score a candidate header row for Homabay payroll / gratuity / pension sheets.
 * Title banners (county name, period labels) score low; real column headers score high.
 */
export function scorePayrollHeaderRow(cells) {
  const labels = (cells || []).map((c) =>
    String(c == null ? "" : c)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
  );
  if (!labels.some(Boolean)) return -100;

  let score = 0;
  const joined = labels.join(" | ");

  // Strong gratuity / identity columns (county schedule template)
  const strong = [
    ["gratuity", 12],
    ["gratuity amount", 12],
    ["idnum", 8],
    ["id num", 8],
    ["id number", 8],
    ["national id", 8],
    ["payroll number", 8],
    ["payrollnum", 8],
    ["pf no", 6],
    ["emp no", 5],
    ["designation", 5],
    ["desig code", 4],
    ["nature of pay", 4],
    ["engagement", 3],
    ["contract end", 3],
    ["dec pay", 3],
    ["name", 3],
    ["sno", 3],
    ["s/no", 3],
    ["s.no", 3]
  ];
  for (const [p, w] of strong) {
    if (labels.some((l) => l === p || l.includes(p))) score += w;
  }

  // General payroll header cues
  const soft = ["basic", "net pay", "gross", "vote", "department", "amount", "deduction"];
  for (const p of soft) {
    if (labels.some((l) => l.includes(p))) score += 2;
  }

  // Penalise title / banner rows
  if (/homa\s*bay|county\s+government|benefits?\s+schedule|payroll\s+report/i.test(joined)) {
    score -= 20;
  }
  if (labels.filter(Boolean).length <= 2 && joined.length > 40) {
    score -= 8;
  }

  // Prefer rows with several non-empty cells
  const filled = labels.filter(Boolean).length;
  score += Math.min(filled, 12);

  return score;
}

/**
 * Convert a 2D cell matrix (rows × cols) into { rows, headers } using the best header row.
 * Homabay gratuity schedule: title rows, then SNO | IDNUM | PAYROLL NUMBER | … | GRATUITY, then data, then TOTAL.
 */
export function matrixToHeaderedRows(matrix, minHeaderScore = 12) {
  const raw = (matrix || []).filter((row) =>
    (row || []).some((c) => String(c == null ? "" : c).trim() !== "")
  );
  if (!raw.length) {
    return { rows: [], headers: [], headerRowIndex: -1 };
  }

  let bestIdx = 0;
  let bestScore = -999;
  const scanLimit = Math.min(raw.length, 25);
  for (let i = 0; i < scanLimit; i++) {
    const sc = scorePayrollHeaderRow(raw[i]);
    if (sc > bestScore) {
      bestScore = sc;
      bestIdx = i;
    }
  }

  // If nothing looks like a header, keep row 0 (classic single-header CSV)
  if (bestScore < minHeaderScore && bestScore <= 0) {
    bestIdx = 0;
  }

  const headerCells = raw[bestIdx] || [];
  const headers = [];
  const used = new Map();
  headerCells.forEach((cell, col) => {
    let name = String(cell == null ? "" : cell).trim();
    if (!name) name = `Column_${col + 1}`;
    const base = name;
    let n = used.get(base) || 0;
    n += 1;
    used.set(base, n);
    if (n > 1) name = `${base}_${n}`;
    headers.push(name);
  });

  // Ensure matrix width covers wider data rows
  const maxCols = Math.max(
    headers.length,
    ...raw.map((r) => (r || []).length)
  );
  while (headers.length < maxCols) {
    headers.push(`Column_${headers.length + 1}`);
  }

  const rows = [];
  for (let i = bestIdx + 1; i < raw.length; i++) {
    const line = raw[i] || [];
    const obj = {};
    let any = false;
    for (let c = 0; c < headers.length; c++) {
      const val = line[c] != null ? line[c] : "";
      obj[headers[c]] = val;
      if (String(val).trim() !== "") any = true;
    }
    if (any) rows.push(obj);
  }

  return { rows, headers, headerRowIndex: bestIdx, headerScore: bestScore };
}

/** Detect earning (8xx) and deduction (9xx) code columns from ledger headers. */
export function detectCodeColumns(headers) {
  const earnings = [];
  const deductions = [];
  for (const h of headers || []) {
    const raw = String(h || "").trim();
    if (!raw) continue;
    // Support "801 Basic" and "Basic Salary 801"
    let m = raw.match(/^(\d{3})\s+(.+)$/);
    if (!m) m = raw.match(/^(.+?)\s+(\d{3})$/);
    if (!m) continue;
    const code = /^\d{3}$/.test(m[1]) ? m[1] : m[2];
    const label = (/^\d{3}$/.test(m[1]) ? m[2] : m[1]).trim();
    const num = parseInt(code, 10);
    if (Number.isNaN(num)) continue;
    const entry = { code, label, header_name: raw };
    if (num >= 800 && num <= 899) earnings.push(entry);
    else if (num >= 900 && num <= 999) deductions.push(entry);
  }
  earnings.sort((a, b) => a.code.localeCompare(b.code));
  deductions.sort((a, b) => a.code.localeCompare(b.code));
  return { earnings, deductions };
}

export function isVoluntaryPensionText(text) {
  const t = String(text || "").toLowerCase();
  if (!t) return false;
  return (
    t.includes("voluntary") ||
    t.includes("avc") ||
    t.includes("additional voluntary") ||
    t.includes("pension voluntary") ||
    /\b957\b/.test(t)
  );
}

export function maskSensitive(value, mask = true) {
  if (!mask || value == null || value === "") return value;
  const s = String(value).trim();
  if (s.length <= 4) return "****";
  return s.slice(0, 2) + "*".repeat(Math.min(8, s.length - 4)) + s.slice(-2);
}

const PAYROLL_FIELD_PATTERNS = {
  payrollNum: ["payrollnum", "payroll num", "pf no", "p/f no", "personal number"],
  idNum: ["idnum", "id num", "national id", "nationalid", "id no"],
  taxPin: ["taxpin", "tax pin", "kra pin", "pin"],
  names: ["names", "employee name", "staff name", "full name", "name"],
  gender: ["gender", "sex"],
  dateHired: ["datehired", "date hired", "hire date", "employment date"],
  designation: ["designation", "position", "job title"],
  jobGroup: ["jobgroup", "job group", "grade", "salary scale"],
  voteCode: ["votecode", "vote code"],
  voteName: ["votename", "vote name"],
  subVoteName: ["subvotename", "sub vote"],
  headName: ["headname", "head name"],
  subHeadName: ["subheadname", "sub head"],
  wCountyName: ["wcountyname", "work county", "county"],
  grossPay: ["grosspay", "gross pay", "gross salary"],
  totalDeductions: ["totaldeductions", "total deductions", "totaldeductions_athird"],
  netPay: ["netpay", "net pay", "net salary"]
};

const PENSION_FIELD_PATTERNS = {
  paydate: ["paydate", "pay date", "payment date"],
  voteCode: ["votecode", "vote code"],
  payrollNum: ["payrollnum", "payroll num", "pf no"],
  idNum: ["idnum", "id num", "national id"],
  edCode: ["edcode", "ed code", "deduction code"],
  amount: ["amount", "deduction", "contribution"],
  balance: ["balance"],
  name: ["name", "employee name"],
  remarks: ["remarks", "remark", "notes"]
};

const GRATUITY_FIELD_PATTERNS = {
  payrollNum: ["payrollnum", "payroll num", "pf no", "emp no"],
  idNum: ["idnum", "id num", "national id"],
  name: ["name", "employee name"],
  amount: ["gratuity", "gratuity amount", "gratuity benefits"],
  voteCode: ["votecode", "vote code"],
  department: ["department", "dept", "directorate"]
};

export function autoMapTemplateFields(headers, templateType) {
  const patterns =
    templateType === "pension"
      ? PENSION_FIELD_PATTERNS
      : templateType === "gratuity"
        ? GRATUITY_FIELD_PATTERNS
        : PAYROLL_FIELD_PATTERNS;
  const out = {};
  for (const [key, pats] of Object.entries(patterns)) {
    out[key] = pickColumn(headers, pats) || "";
  }
  return out;
}

export function buildRegisterIndex(registerList) {
  const byId = new Map();
  for (const e of registerList) {
    const a = normId(e.empNo);
    const b = normId(e.nationalId);
    if (a) byId.set(a, e);
    if (b) byId.set(b, e);
  }
  return byId;
}

/**
 * Register department Vote = departments.code (UI “Vote”).
 * Prefer this over any payroll ledger VOTE column.
 */
export function resolveRegisterVote(emp, deptVoteById = null) {
  if (!emp) return "";
  // Direct fields if already denormalized onto employee
  const direct =
    emp.departmentVote ||
    emp.departmentCode ||
    emp.voteCode ||
    emp.vote ||
    emp.deptVote ||
    "";
  if (String(direct).trim() !== "") return String(direct).trim();

  const id = emp.departmentID ?? emp.departmentId ?? null;
  if (id == null || id === "" || id === 0) return "";
  if (!deptVoteById) return "";
  if (deptVoteById instanceof Map) return String(deptVoteById.get(String(id)) || "").trim();
  return String(deptVoteById[String(id)] || deptVoteById[id] || "").trim();
}

/**
 * Spending entity = Employee Register department name (canonical).
 * Never mix departmentID with departmentName as Map keys — that breaks enrichment.
 * Do NOT substitute work station / VoteName / WStationName when the employee has no department.
 * @param {object|null} emp
 * @param {string} [csvDept] kept for call-site compatibility; never used as entity
 * @param {Map|object|null} deptNameById optional id → name map from departments table
 */
export function resolveSpendingEntity(emp, csvDept = "", deptNameById = null) {
  void csvDept;
  const lookupDept = (id) => {
    if (id == null || id === "" || id === 0 || id === "0") return "";
    if (!deptNameById) return "";
    if (deptNameById instanceof Map) return String(deptNameById.get(String(id)) || "").trim();
    return String(deptNameById[String(id)] || deptNameById[id] || "").trim();
  };

  if (!emp) {
    // Extra on payroll — not in register. Do not invent an entity from station.
    return "(Unknown — not in register)";
  }

  let name = String(emp.departmentName || emp.department || "").trim();
  // Reject pure numeric "names" (often departmentIDs incorrectly stored in name fields)
  if (name && /^\d+$/.test(name)) {
    const mapped = lookupDept(name);
    name = mapped || "";
  }
  if (name && !/^\d+$/.test(name)) return name;

  const id = emp.departmentID ?? emp.departmentId ?? null;
  const fromMap = lookupDept(id);
  if (fromMap) return fromMap;
  if (id != null && String(id).trim() !== "" && String(id) !== "0") {
    return `Department #${String(id).trim()}`;
  }

  // No register department — leave blank bucket (never force workstation / vote from CSV)
  return "(No department)";
}

/**
 * Prefer coded ledger columns.
 * Homabay ledgers use both "801 Basic Salary" and "Basic Salary 801".
 */
export function pickLedgerColumn(headers, codeHints = [], labelHints = []) {
  const list = headers || [];
  for (const code of codeHints) {
    const c = String(code);
    const reStart = new RegExp(`^${c}\\b`, "i");
    const reEnd = new RegExp(`\\b${c}\\s*$`, "i");
    const reParen = new RegExp(`\\(${c}\\)`, "i");
    const hit =
      list.find((h) => reStart.test(String(h || "").trim())) ||
      list.find((h) => reEnd.test(String(h || "").trim())) ||
      list.find((h) => reParen.test(String(h || "").trim())) ||
      list.find((h) => new RegExp(`\\b${c}\\b`).test(String(h || "")));
    if (hit) return hit;
  }
  return pickColumn(list, labelHints);
}

/** Resolve standard payroll ledger amount columns (Homabay field names). */
export function resolvePayrollCostColumns(headers) {
  const h = headers || [];
  return {
    basic: pickLedgerColumn(h, ["801"], ["basic salary", "basic pay", "basic"]),
    rental: pickLedgerColumn(h, ["802"], ["rental house all", "rental house", "rental"]),
    commuter: pickLedgerColumn(h, ["813"], ["commuter all", "commuter"]),
    gross: pickColumn(h, ["grosspay", "gross pay", "gross salary", "gross"]),
    nssf:
      pickLedgerColumn(h, ["933"], ["nssf auto", "nssf (auto)", "nssf"]) ||
      pickColumn(h, ["nssf auto", "nssf"]),
    housing:
      pickLedgerColumn(h, ["944"], ["housing levy fund", "housing levy", "h/levy", "h levy"]) ||
      pickColumn(h, ["housing levy fund", "housing levy", "h/levy"]),
    pensionEmployer:
      pickLedgerColumn(h, ["902"], ["pension (auto)", "pension auto", "pension (employer)", "employer pension"]) ||
      pickColumn(h, ["pension (auto)", "pension (employer)", "employer pension"]),
    nita: pickColumn(h, ["nita", "n.i.t.a"]),
    voteCode: pickColumn(h, ["votecode", "vote code"]),
    id: pickColumn(h, ["payrollnum", "idnum", "pf", "emp", "staff", "employee", "national"]),
    name: pickColumn(h, ["names", "employee name", "staff name", "full name", "name"])
  };
}

/**
 * Bottom "TOTAL WAGE BILL" block matching docus Spending entity (Costings) Summary.
 * Gross + employer obligations (NSSF, Housing Levy, Pension, NITA, Gratuity from upload total).
 */
export function buildWageBillBottomSummary(spendingSummaryRows, gratuityTotal = 0) {
  const sum = (key) =>
    (spendingSummaryRows || []).reduce((s, r) => s + (Number(r[key]) || 0), 0);
  const monthly = {
    staffCount: sum("staffCount"),
    basic: sum("basic"),
    rental: sum("rental"),
    commuter: sum("commuter"),
    otherAllow: sum("otherAllow"),
    gross: sum("gross"),
    nssf: sum("nssf"),
    housing: sum("housing"),
    pensionEmployer: sum("pensionEmployer"),
    nita: sum("nita"),
    gratuity: Number(gratuityTotal) || 0
  };
  monthly.employerObligations =
    monthly.nssf +
    monthly.housing +
    monthly.pensionEmployer +
    monthly.nita +
    monthly.gratuity;
  monthly.totalWageBill = monthly.gross + monthly.employerObligations;
  const annual = {
    staffCount: monthly.staffCount,
    basic: monthly.basic * 12,
    rental: monthly.rental * 12,
    commuter: monthly.commuter * 12,
    otherAllow: monthly.otherAllow * 12,
    gross: monthly.gross * 12,
    nssf: monthly.nssf * 12,
    housing: monthly.housing * 12,
    pensionEmployer: monthly.pensionEmployer * 12,
    nita: monthly.nita * 12,
    gratuity: monthly.gratuity * 12,
    employerObligations: monthly.employerObligations * 12,
    totalWageBill: monthly.totalWageBill * 12
  };
  return { monthly, annual };
}

/**
 * Gratuity total for wage bill: use the GRATUITY figure on the TOTAL / GRAND TOTAL row only.
 * Do not sum every line amount (that double-counts when a total row already exists).
 * Instruction to users: add a TOTAL row at the bottom of the GRATUITY column before upload.
 *
 * Homabay layout: TOTAL often sits under IDNUM (col B), figure under GRATUITY (last col).
 */
export function sumUploadedGratuity(gratuityRows, headers) {
  const cols = headers || [];
  let amtCol = null;
  for (const h of cols) {
    const k = normKeyHeader(h);
    if (k === "gratuity" || k === "gratuity amount" || k === "gratuity benefits") {
      amtCol = h;
      break;
    }
  }
  if (!amtCol) {
    for (const h of cols) {
      const k = normKeyHeader(h);
      if (k.includes("gratuity") && !k.includes("engage") && !k.includes("nature")) {
        amtCol = h;
        break;
      }
    }
  }
  // Homabay schedule last money column is often just GRATUITY; avoid May/Dec Pay
  if (!amtCol) {
    for (const h of cols) {
      const k = normKeyHeader(h);
      if (
        (k === "amount" || k === "total" || k.endsWith(" amount")) &&
        !/may pay|month pay|basic|dec pay|pay$/i.test(String(h))
      ) {
        amtCol = h;
        break;
      }
    }
  }

  const empty = {
    total: 0,
    amountColumn: amtCol,
    source: "none",
    totalRowFound: false,
    hasGratuityColumn: !!amtCol,
    totalRowIndex: -1
  };

  if (!amtCol || !(gratuityRows || []).length) {
    return empty;
  }

  const isTotalLabel = (text) => {
    const t = String(text || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    if (!t) return false;
    // Strong matches (incl. Homabay TOTAL under IDNUM)
    if (/^(grand\s+)?total(s)?$/.test(t)) return true;
    if (/^total\s+(row|amount|gratuity|pay|figure)?$/.test(t)) return true;
    if (t === "g.total" || t === "g total" || t === "gtotal") return true;
    if (t === "totals" || t === "total amount") return true;
    // Soft: cells that clearly mark a total line without employee-level noise
    if (/total/i.test(t) && !/\d{5,}/.test(t) && t.length < 40) {
      if (/\b(grand|sub|overall|aggregate)\b/.test(t) || /^total\b/.test(t)) return true;
    }
    return false;
  };

  const rowLooksLikeTotal = (row) => {
    if (!row || typeof row !== "object") return false;
    for (const h of cols) {
      if (isTotalLabel(row[h])) return true;
    }
    for (const v of Object.values(row)) {
      if (isTotalLabel(v)) return true;
    }
    return false;
  };

  // Prefer last total-like row (grand total usually at bottom)
  let totalRow = null;
  let totalRowIndex = -1;
  (gratuityRows || []).forEach((row, idx) => {
    if (rowLooksLikeTotal(row)) {
      totalRow = row;
      totalRowIndex = idx;
    }
  });

  if (totalRow) {
    // Primary: GRATUITY column; if blank/"-", search any money cell on the total row
    let figure = parseMoney(totalRow[amtCol]);
    if (!figure || figure === 0) {
      for (const h of cols) {
        if (h === amtCol) continue;
        const k = normKeyHeader(h);
        if (/sno|s\/no|idnum|id num|name|designation|engage|nature|desig|payroll/i.test(k)) continue;
        const n = parseMoney(totalRow[h]);
        if (n > 0) {
          figure = n;
          break;
        }
      }
    }
    return {
      total: figure,
      amountColumn: amtCol,
      source: "total_row",
      totalRowFound: true,
      hasGratuityColumn: true,
      totalRowIndex,
      totalAmountMissing: !(figure > 0)
    };
  }

  // No TOTAL row — do not invent a total (callers must reject load/analyze)
  return {
    total: 0,
    amountColumn: amtCol,
    source: "missing_total_row",
    totalRowFound: false,
    hasGratuityColumn: true,
    totalRowIndex: -1
  };
}

/**
 * Hard gate for gratuity file upload / analysis.
 * Requires a GRATUITY (amount) column and a TOTAL / GRAND TOTAL row with that figure filled.
 */
export function validateGratuityFileForAnalysis(gratuityRows, headers) {
  const summed = sumUploadedGratuity(gratuityRows, headers);
  if (!summed.hasGratuityColumn) {
    return {
      ok: false,
      message:
        "GRATUITY FILE REJECTED: Could not find a GRATUITY column. Put the word GRATUITY on the header row (with SNO, IDNUM, PAYROLL NUMBER, NAME…) then a TOTAL row at the bottom with the grand-total figure in the GRATUITY column. Title rows above the headers are fine."
    };
  }
  if (!summed.totalRowFound) {
    return {
      ok: false,
      message:
        "GRATUITY FILE REJECTED: Add a TOTAL (or GRAND TOTAL) row at the bottom. Put TOTAL in any column (e.g. under IDNUM) and the grand total number in the GRATUITY column. Placeholders like \"-\" alone are not enough — enter the total amount."
    };
  }
  if (summed.totalAmountMissing || !(summed.total > 0)) {
    return {
      ok: false,
      message:
        "GRATUITY FILE REJECTED: TOTAL row found but the GRATUITY column has no amount (empty or \"-\"). Enter the total gratuity figure in the GRATUITY cell of the TOTAL row, then re-upload."
    };
  }
  return {
    ok: true,
    message: "",
    total: summed.total,
    amountColumn: summed.amountColumn,
    summed
  };
}

export function findDuplicatePayrollIds(csvRows, idCol) {
  const seen = new Map();
  const dups = [];
  if (!idCol) return dups;
  for (let i = 0; i < csvRows.length; i++) {
    const nid = normId(csvRows[i][idCol]);
    if (!nid) continue;
    if (seen.has(nid)) {
      dups.push({ staffId: nid, rowIndexes: [seen.get(nid), i] });
    } else {
      seen.set(nid, i);
    }
  }
  return dups;
}

function strEq(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

function isInactiveStatus(status) {
  const s = String(status || "").toLowerCase();
  return ["inactive", "terminated", "retired", "separated", "resigned", "deceased", "dismissed"].some((x) =>
    s.includes(x)
  );
}

/**
 * Core register vs payroll reconciliation.
 * Spending entity for matched employees = register departmentName.
 */
export function runRegisterPayrollAnalysis({
  csvRows,
  headers,
  registerList,
  columnMaps = {},
  nitaPerEmployee = 0,
  deptNameById = null
}) {
  const mapId = columnMaps.mapId || pickColumn(headers, ["payrollnum", "idnum", "pf", "emp", "staff", "national", "employee no", "personal number"]);
  const mapDept =
    columnMaps.mapDept ||
    pickColumn(headers, ["department", "dept", "wstationname", "wstation", "work station", "station", "directorate", "votename", "vote name"]);
  const mapPay =
    columnMaps.mapPay ||
    pickColumn(headers, ["grosspay", "gross pay", "gross salary", "gross"]) ||
    pickColumn(headers, ["netpay", "net pay", "net"]) ||
    pickLedgerColumn(headers, ["801"], ["basic salary", "basic pay", "basic"]);
  const mapName = columnMaps.mapName || pickColumn(headers, ["names", "employee name", "staff name", "full name", "name"]);
  const mapDesignation = columnMaps.mapDesignation || pickColumn(headers, ["designation", "position", "job title"]);
  const mapJobGroup = columnMaps.mapJobGroup || pickColumn(headers, ["jobgroup", "job group", "grade"]);

  const grossCol =
    pickColumn(headers, ["grosspay", "gross pay", "gross salary", "gross"]) ||
    (mapPay && /gross/i.test(String(mapPay)) ? mapPay : null);
  const netCol = pickColumn(headers, ["netpay", "net pay", "net salary", "net"]);
  const dedCol = pickColumn(headers, ["totaldeductions", "total deductions"]);
  const pension902Col =
    pickLedgerColumn(headers, ["902"], ["902 pension", "902 pension (auto)", "pension (auto)"]) ||
    pickColumn(headers, ["902 pension", "902 pension (auto)", "pension (auto)"]) ||
    null;
  const nitaCol = pickColumn(headers, ["nita", "n.i.t.a"]);

  const { earnings, deductions } = detectCodeColumns(headers);
  const byId = buildRegisterIndex(registerList);
  const matchedIds = new Set();
  const duplicateRows = findDuplicatePayrollIds(csvRows, mapId);

  let totalGross = 0;
  let totalNet = 0;
  let totalDeductions = 0;
  let totalPayrollPension902 = 0;

  const wageMap = new Map();
  const spendingMap = new Map();
  const csvOnly = [];
  const matchedDetail = [];
  const deptVariances = [];
  const designationVariances = [];
  const jobGroupVariances = [];
  const inactivePaid = [];

  for (const row of csvRows) {
    const rawId = mapId ? row[mapId] : null;
    const nid = normId(rawId);
    const pay = mapPay ? parseMoney(row[mapPay]) : 0;
    const gross = grossCol ? parseMoney(row[grossCol]) : pay;
    const net = netCol ? parseMoney(row[netCol]) : 0;
    const ded = dedCol ? parseMoney(row[dedCol]) : 0;
    const pension902 = pension902Col ? parseMoney(row[pension902Col]) : 0;

    totalGross += gross;
    totalNet += net || pay;
    totalDeductions += ded;
    totalPayrollPension902 += pension902;

    const csvDept = (mapDept && row[mapDept] != null ? String(row[mapDept]).trim() : "") || "";
    const nm = mapName ? String(row[mapName] || "").trim() : "";
    const csvDesig = mapDesignation ? String(row[mapDesignation] || "").trim() : "";
    const csvJg = mapJobGroup ? String(row[mapJobGroup] || "").trim() : "";

    const emp = nid ? byId.get(nid) : null;
    // Canonical costing entity = register department (with id→name map support)
    const spendingEntity = resolveSpendingEntity(emp, csvDept, deptNameById);

    // Wage-by-csv-station (secondary); still keep for CSV station summary
    const wageKey = spendingEntity;
    const prevW = wageMap.get(wageKey) || { total: 0, count: 0, gross: 0, net: 0 };
    prevW.total += pay;
    prevW.gross += gross;
    prevW.net += net || pay;
    prevW.count += 1;
    wageMap.set(wageKey, prevW);

    const sp = spendingMap.get(spendingEntity) || {
      entity: spendingEntity,
      staffCount: 0,
      gross: 0,
      net: 0,
      deductions: 0,
      pension902: 0,
      nita: 0
    };
    sp.staffCount += 1;
    sp.gross += gross;
    sp.net += net || pay;
    sp.deductions += ded;
    sp.pension902 += pension902;
    spendingMap.set(spendingEntity, sp);

    if (emp) {
      const e1 = normId(emp.empNo);
      const e2 = normId(emp.nationalId);
      if (e1) matchedIds.add(e1);
      if (e2) matchedIds.add(e2);
      const regDept = resolveSpendingEntity(emp, "", deptNameById);
      const deptMismatch = csvDept && regDept && !strEq(regDept, csvDept);
      const desigMismatch = csvDesig && emp.designation && !strEq(emp.designation, csvDesig);
      const jgMismatch = csvJg && emp.jobGroup && !strEq(emp.jobGroup, csvJg);
      const inactiveIssue = isInactiveStatus(emp.status);

      const detail = {
        empNo: emp.empNo,
        nationalId: emp.nationalId,
        regName: [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ").trim(),
        regDept,
        spendingEntity,
        csvDept,
        deptMismatch,
        regDesignation: emp.designation || "",
        csvDesignation: csvDesig,
        designationMismatch: desigMismatch,
        regJobGroup: emp.jobGroup || "",
        csvJobGroup: csvJg,
        jobGroupMismatch: jgMismatch,
        status: emp.status,
        inactivePaid: inactiveIssue,
        pay,
        gross,
        net: net || pay,
        pension902
      };
      matchedDetail.push(detail);
      if (deptMismatch) deptVariances.push(detail);
      if (desigMismatch) designationVariances.push(detail);
      if (jgMismatch) jobGroupVariances.push(detail);
      if (inactiveIssue) inactivePaid.push(detail);
    } else {
      csvOnly.push({ staffId: rawId, name: nm, dept: csvDept, spendingEntity, pay, gross });
    }
  }

  const registerOnly = registerList.filter((e) => {
    const k1 = normId(e.empNo);
    const k2 = normId(e.nationalId);
    return !((k1 && matchedIds.has(k1)) || (k2 && matchedIds.has(k2)));
  });

  const employeeCount = matchedDetail.length;
  const nitaExpected = employeeCount * (Number(nitaPerEmployee) || 0);
  let nitaActual = 0;
  if (nitaCol) {
    for (const row of csvRows) nitaActual += parseMoney(row[nitaCol]);
  } else {
    nitaActual = nitaExpected;
  }

  for (const sp of spendingMap.values()) {
    sp.nita = sp.staffCount * (Number(nitaPerEmployee) || 0);
    sp.totalCost = sp.gross + sp.pension902 + sp.nita;
  }

  const spendingSummaryRows = Array.from(spendingMap.values()).sort((a, b) =>
    String(a.entity).localeCompare(String(b.entity))
  );
  spendingSummaryRows.forEach((r, i) => {
    r.seq = i + 1;
    r.key = String(r.entity).toLowerCase();
  });

  // Wage bill rows = register spending entities (same canonical mapping as costings)
  const wageBillRows = Array.from(wageMap.entries())
    .map(([dept, v]) => ({
      dept,
      entity: dept,
      total: v.total,
      gross: v.gross,
      net: v.net,
      count: v.count,
      staffCount: v.count
    }))
    .sort((a, b) => b.total - a.total);

  const financialSummary = {
    grossPay: totalGross,
    totalDeductions,
    netPay: totalNet,
    payrollPension902: totalPayrollPension902,
    nitaExpected,
    nitaActual,
    nitaVariance: nitaActual - nitaExpected,
    employeeCount
  };

  const summary = {
    csvRowCount: csvRows.length,
    registerCount: registerList.length,
    matchedCount: matchedDetail.length,
    csvOnlyCount: csvOnly.length,
    registerOnlyCount: registerOnly.length,
    duplicateCount: duplicateRows.length,
    deptVarianceCount: deptVariances.length,
    designationVarianceCount: designationVariances.length,
    jobGroupVarianceCount: jobGroupVariances.length,
    inactivePaidCount: inactivePaid.length,
    totalCsvPay: totalNet || totalGross,
    ...financialSummary
  };

  const codesForCatalog = [
    ...earnings.map((e) => ({ code: e.code, code_type: "earning", label: e.label, header_name: e.header_name })),
    ...deductions.map((d) => ({ code: d.code, code_type: "deduction", label: d.label, header_name: d.header_name }))
  ];

  return {
    summary,
    financialSummary,
    wageBillRows,
    registerOnlyRows: registerOnly,
    csvOnlyRows: csvOnly,
    matchedDetailRows: matchedDetail,
    spendingSummaryRows,
    duplicateRows,
    deptVariances,
    designationVariances,
    jobGroupVariances,
    inactivePaidRows: inactivePaid,
    earnings,
    deductions,
    pension902Col,
    codesForCatalog,
    columnMapsResolved: { mapId, mapDept, mapPay, mapName, mapDesignation, mapJobGroup }
  };
}

/** Parse pension rows from up to 4 files; reconcile mandatory EDCode 902 only. */
export function reconcilePension({ payrollMatchedRows, pensionFileRowsList, pensionMapsList }) {
  // Index payroll once per employee (avoid double-count when both empNo + nationalId are keys)
  const payrollByPrimary = new Map();
  const payrollAlias = new Map();
  for (const r of payrollMatchedRows || []) {
    const k1 = normId(r.empNo);
    const k2 = normId(r.nationalId);
    const primary = k1 || k2;
    if (!primary) continue;
    const amt = Number(r.pension902) || 0;
    if (!payrollByPrimary.has(primary)) {
      payrollByPrimary.set(primary, { ...r, pensionPayroll: amt, primary });
    }
    const rec = payrollByPrimary.get(primary);
    if (k1) payrollAlias.set(k1, rec);
    if (k2) payrollAlias.set(k2, rec);
  }

  const pensionRecords = [];
  const filesArray = Array.isArray(pensionFileRowsList)
    ? pensionFileRowsList
    : typeof pensionFileRowsList === "object" && pensionFileRowsList !== null
      ? Object.values(pensionFileRowsList)
      : [];

  for (let fi = 0; fi < filesArray.length; fi++) {
    const rows = filesArray[fi] || [];
    const maps = (pensionMapsList && pensionMapsList[fi]) || autoMapTemplateFields(rows[0] ? Object.keys(rows[0]) : [], "pension");
    const edCol = maps.edCode || pickColumn(Object.keys(rows[0] || {}), ["edcode", "ed code"]);
    const amtCol = maps.amount || pickColumn(Object.keys(rows[0] || {}), ["amount", "contribution", "deduction"]);
    const idCol =
      maps.payrollNum ||
      maps.idNum ||
      pickColumn(Object.keys(rows[0] || {}), ["payrollnum", "idnum", "pf no", "national id"]);
    const nameCol = maps.name;
    const remarksCol = maps.remarks || pickColumn(Object.keys(rows[0] || {}), ["remarks", "remark", "notes"]);
    const balanceCol = maps.balance || pickColumn(Object.keys(rows[0] || {}), ["balance"]);

    for (const row of rows) {
      const edRaw = edCol ? String(row[edCol] ?? "").trim() : "";
      const edCode = edRaw.replace(/\D/g, "").slice(0, 3) || edRaw;
      if (edCode !== "902" && !/^902\b/.test(edRaw)) continue;

      const remarks = remarksCol ? String(row[remarksCol] || "") : "";
      const nameVal = nameCol ? String(row[nameCol] || "") : "";
      if (isVoluntaryPensionText(remarks) || isVoluntaryPensionText(nameVal) || isVoluntaryPensionText(edRaw)) continue;

      const rawId = idCol ? row[idCol] : "";
      const key = normId(rawId);
      if (!key) continue;

      let amt = 0;
      if (balanceCol && remarks.toLowerCase().trim() === "auto") {
        amt = parseMoney(row[balanceCol]);
      } else {
        amt = amtCol ? parseMoney(row[amtCol]) : 0;
      }
      
      pensionRecords.push({ key, rawId, amount: amt, fileIndex: fi + 1, name: nameVal });
    }
  }

  const pensionByKey = new Map();
  for (const p of pensionRecords) {
    const prev = pensionByKey.get(p.key) || { ...p, amount: 0 };
    prev.amount += p.amount;
    pensionByKey.set(p.key, prev);
  }

  const matched = [];
  const missingInPension = [];
  const missingInPayroll = [];
  const variances = [];
  let payrollTotal = 0;
  let pensionTotal = 0;

  const matchedPayrollPrimaries = new Set();
  const matchedPensionKeys = new Set();

  for (const [primary, pr] of payrollByPrimary) {
    const pAmt = Number(pr.pensionPayroll) || 0;
    payrollTotal += pAmt;

    const pe =
      pensionByKey.get(primary) ||
      (normId(pr.empNo) ? pensionByKey.get(normId(pr.empNo)) : null) ||
      (normId(pr.nationalId) ? pensionByKey.get(normId(pr.nationalId)) : null) ||
      null;

    if (pe) {
      matchedPayrollPrimaries.add(primary);
      matchedPensionKeys.add(pe.key);
      const eAmt = Number(pe.amount) || 0;
      const variance = pAmt - eAmt;
      const row = {
        key: primary,
        empNo: pr.empNo,
        nationalId: pr.nationalId,
        name: pr.regName,
        department: pr.spendingEntity || pr.regDept,
        payrollPension: pAmt,
        pensionFile: eAmt,
        variance,
        status: Math.abs(variance) < 0.01 ? "matched" : variance > 0 ? "overpayment" : "underpayment"
      };
      matched.push(row);
      if (Math.abs(variance) >= 0.01) variances.push(row);
    } else {
      missingInPension.push({
        key: primary,
        empNo: pr.empNo,
        nationalId: pr.nationalId,
        name: pr.regName,
        department: pr.spendingEntity,
        payrollPension: pAmt
      });
    }
  }

  for (const [key, pe] of pensionByKey) {
    if (matchedPensionKeys.has(key)) continue;
    const pr = payrollAlias.get(key);
    if (pr && matchedPayrollPrimaries.has(pr.primary)) continue;
    missingInPayroll.push({
      key,
      rawId: pe.rawId,
      name: pe.name,
      pensionFile: Number(pe.amount) || 0
    });
  }

  pensionTotal = 0;
  for (const pe of pensionByKey.values()) {
    pensionTotal += Number(pe.amount) || 0;
  }

  return {
    matched,
    missingInPension,
    missingInPayroll,
    variances,
    totals: {
      payrollPension902: payrollTotal,
      pensionFiles902: pensionTotal,
      variance: payrollTotal - pensionTotal,
      matchedCount: matched.length,
      varianceCount: variances.length
    },
    pensionRecordCount: pensionRecords.length
  };
}

/** Gratuity reconciliation vs payroll/register. Total figure from TOTAL row GRATUITY column. */
export function reconcileGratuity({ gratuityRows, headers, registerList, payrollMatchedRows }) {
  const maps = autoMapTemplateFields(headers, "gratuity");
  const idCol = maps.payrollNum || maps.idNum;
  const summed = sumUploadedGratuity(gratuityRows, headers);
  const amtCol = summed.amountColumn || maps.amount;
  const byId = buildRegisterIndex(registerList);
  const payrollByKey = new Map();
  for (const r of payrollMatchedRows || []) {
    const k1 = normId(r.empNo);
    const k2 = normId(r.nationalId);
    if (k1) payrollByKey.set(k1, r);
    if (k2) payrollByKey.set(k2, r);
  }

  const isTotalish = (row) => {
    if (!row) return false;
    for (const v of Object.values(row)) {
      const t = String(v || "").trim().toLowerCase();
      if (/^(grand\s+)?total(s)?$/.test(t) || /^total\b/.test(t)) return true;
    }
    return false;
  };

  const uploaded = [];
  for (const row of gratuityRows || []) {
    if (isTotalish(row)) continue; // exclude total row from detail list
    const rawId = idCol ? row[idCol] : null;
    const key = normId(rawId);
    const amt = amtCol ? parseMoney(row[amtCol]) : 0;
    const emp = key ? byId.get(key) : null;
    uploaded.push({
      key,
      rawId,
      amount: amt,
      name: maps.name ? row[maps.name] : "",
      department: emp ? emp.departmentName : maps.department ? row[maps.department] : "",
      inRegister: !!emp,
      inPayroll: key ? payrollByKey.has(key) : false
    });
  }

  const missingInGratuity = (payrollMatchedRows || [])
    .filter((r) => {
      const k1 = normId(r.empNo);
      const k2 = normId(r.nationalId);
      return ![k1, k2].some((k) => k && uploaded.some((u) => u.key === k));
    })
    .map((r) => ({
      empNo: r.empNo,
      nationalId: r.nationalId,
      name: r.regName,
      department: r.spendingEntity
    }));

  return {
    uploadedRows: uploaded,
    uploadedTotal: summed.total,
    amountColumn: amtCol || "",
    totalSource: summed.source || "none",
    totalRowFound: !!summed.totalRowFound,
    missingInGratuity,
    missingCount: missingInGratuity.length
  };
}

export function buildExecutiveSummary({
  financialSummary,
  pensionRecon,
  gratuityRecon,
  spendingSummaryRows,
  variancesTopN = 10
}) {
  const fs = financialSummary || {};
  const pr = pensionRecon?.totals || {};
  const gr = gratuityRecon || {};
  const allVariances = [...(pensionRecon?.variances || [])];
  allVariances.sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
  const topVariances = allVariances.slice(0, variancesTopN);

  const spendingEntitySummary = (spendingSummaryRows || []).map((r) => ({
    department: r.entity,
    employeeCount: r.staffCount,
    payrollCost: Number(r.gross) || 0,
    basicPay: Number(r.basic) || 0,
    pensionCost: Number(r.pensionEmployer != null ? r.pensionEmployer : r.pension902) || 0,
    nitaCost: Number(r.nita) || 0,
    totalCost: Number(r.total != null ? r.total : r.totalCost) || 0
  }));

  return {
    totalPayrollCost: fs.grossPay || 0,
    totalBasicPay: spendingEntitySummary.reduce((s, r) => s + (Number(r.basicPay) || 0), 0),
    totalPensionCost: pr.pensionFiles902 || fs.payrollPension902 || 0,
    totalGratuityCost: gr.uploadedTotal || 0,
    totalNitaCost: fs.nitaExpected || fs.nitaActual || 0,
    totalVariance: (pr.variance || 0) + (fs.nitaVariance || 0),
    employeeCount: fs.employeeCount || 0,
    topVariances,
    spendingEntitySummary
  };
}
